package org.lennycodes.hldi.components

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flex
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaFacebook
import com.varabyte.kobweb.silk.components.icons.fa.FaInstagram
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.*
import org.lennycodes.hldi.constants.HLDITheme
import org.lennycodes.hldi.constants.RNInfo
import org.lennycodes.hldi.pages.admin.currentYear
import org.lennycodes.hldi.styles.*
import org.w3c.dom.SMOOTH
import org.w3c.dom.ScrollBehavior
import org.w3c.dom.ScrollToOptions

@Composable
fun Footer() {
    Footer(
        attrs = Modifier
            .margin(top = 60.px)
            .padding(60.px, 20.px, 30.px)
            .then(FooterStyle.toModifier())
            .toAttrs()
    ) {
        // wave SVG goes first, absolutely positioned at the top edge
        FooterWaveSvg()
        Div(
            attrs = {
                style {
                    maxWidth(1200.px)
                    margin(0.px, autoLength)
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fit, minmax(250px, 1fr))")
                    gap(40.px)
                }
            }
        ) {
            // About Column
            Div {
                H3(
                    attrs = {
                        style {
                            fontWeight(FontWeight.Bold)
                            fontSize(24.px)
                            fontFamily("Playfair Display", "serif")
                            marginBottom(20.px)
                            color(HLDITheme.Colors.accent)
                        }
                    }
                ) {
                    Text("RN-HLDI")
                }
                P(
                    attrs = {
                        style {
                            fontSize(14.px)
                            lineHeight(LineHeight.Normal)
                            opacity(0.9)
                            marginBottom(20.px)
                            textAlign(TextAlign.Justify)
                        }
                    }
                ) {
                    Text("RN-HLDI is a Non-Governmental, Non-Profit, and Non-Partisan Organization committed to protecting human life, promoting social well-being, and supporting development. Established in 2025, it carries forward the humanitarian work of the former Human Life Defence Department of Rulenge-Ngara Catholic Diocese.")
                }
            }

            // Quick Links
            Div {
                H3(
                    attrs = {
                        style {
                            fontWeight(FontWeight.Bold)
                            fontSize(18.px)
                            fontFamily("Playfair Display", "serif")
                            marginBottom(20.px)
                            color(HLDITheme.Colors.accent)
                        }
                    }
                ) {
                    Text("Useful Links")
                }
                listOf(
                    "Rulenge Ngara Diocese" to "https://www.rulengengaradiocese.or.tz/",
                    "Tanzania NGOs IS" to "https://nis.jamii.go.tz/login",
                    "Tanzania Association of NGOs" to "https://www.tango.or.tz/",
                    "Tanzania Ministry of Health" to "https://www.moh.go.tz/"
                ).forEach { (name, path) ->
                    A(
                        href = path,
                        attrs = Modifier.then(UsefulLinkStyle.toModifier())
                            .toAttrs {
                                attr("target", "_blank")
                                attr("rel", "noopener noreferrer")
                            }
                    ) {
                        Text(name)
                    }
                }
            }

            // Contact Info
            Div {
                H3(
                    attrs = {
                        style {
                            fontWeight(FontWeight.Bold)
                            fontSize(18.px)
                            fontFamily("Playfair Display", "serif")
                            marginBottom(20.px)
                            color(HLDITheme.Colors.accent)
                        }
                    }
                ) {
                    Text("Contact Us")
                }
                listOf(
                    "📍" to RNInfo.address,
                    "☎" to RNInfo.phone[0],
                    "☎" to RNInfo.phone[1],
                    "📠" to RNInfo.fax,
                    "✉️" to RNInfo.email,
                    "🌍" to RNInfo.website
                ).forEach { (icon, text) ->
                    Div(
                        attrs = {
                            style {
                                padding(8.px, 0.px)
                                fontSize(14.px)
                                opacity(0.9)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                            }
                        }
                    ) {
                        // aria-hidden so screen readers don't announce the
                        // emoji's literal name on top of the text that follows
                        Span(attrs = { attr("aria-hidden", "true") }) { Text(icon) }
                        Span { Text(text) }
                    }
                }
            }
            // Newsletter
            Div {
                H3(
                    attrs = {
                        style {
                            fontWeight(FontWeight.Bold)
                            fontSize(18.px)
                            fontFamily("Playfair Display", "serif")
                            marginBottom(20.px)
                            color(HLDITheme.Colors.accent)
                        }
                    }
                ) {
                    Text("Stay Updated")
                }
                P(
                    attrs = {
                        style {
                            fontSize(14.px)
                            opacity(0.9)
                            marginBottom(20.px)
                        }
                    }
                ) {
                    Text("Subscribe to our newsletter for updates on our work and impact.")
                }
                Form(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            flexWrap(FlexWrap.Wrap)
                            gap(8.px)
                        }
                    }
                ) {
                    // Visually-hidden label: keeps the input accessible to
                    // screen readers without changing the visual design
                    Label(
                        forId = "newsletter-email",
                        attrs = {
                            style {
                                position(Position.Absolute)
                                width(1.px)
                                height(1.px)
                                overflow("hidden")
                                property("clip", "rect(0 0 0 0)")
                                whiteSpace("nowrap")
                            }
                        }
                    ) {
                        Text("Email address")
                    }
                    Input(
                        type = InputType.Email,
                        attrs = Modifier
                            .padding(12.px)
                            .flex(1)
                            .minWidth(180.px)
                            .then(NewsletterInputStyle.toModifier())
                            .toAttrs {
                                id("newsletter-email")
                                placeholder("Your email")
                            }
                    )
                    Button(
                        attrs = Modifier.padding(12.px, 20.px)
                            .then(SubscribeButtonStyle.toModifier())
                            .toAttrs()
                    ) {
                        Text("Subscribe")
                    }
                }
            }
        }
        Div(attrs = FooterSocialLinksRowStyle.toAttrs{}) {
            listOf(
                @Composable { modifier: Modifier -> FaInstagram(modifier = modifier) } to "https://www.instagram.com/rn_hldi_tz/",
                @Composable { modifier: Modifier -> FaFacebook(modifier = modifier) } to "https://web.facebook.com/profile.php?id=61592315222621",
            ).forEach { (iconContent, url) ->
                A(
                    href = url,
                    attrs = FooterSocialIconButtonStyle.toAttrs {
                        target(ATarget.Blank)
                    }
                ) {
                    iconContent(Modifier)
                }
            }
        }
        Div(
            attrs =
                Modifier.padding(top = 30.px)
                    .then(BottomBarStyle.toModifier())
                    .toAttrs()
        ) {
            Text("© ${currentYear()} Human Life Defence Initiative. All rights reserved.")
            Span(attrs = { style { margin(0.px, 6.px) } }) { Text("•") } // small separator, optional
            A(
                href = "https://www.lennycodes.dev", // swap in your actual site URL
                attrs = DeveloperCreditLinkStyle.toAttrs {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                }
            ) {
                Text("Developed by lennycodes")
            }
        }
    }

    BackToTopButton()
}
@Composable
fun BackToTopButton() {
    var isVisible by remember { mutableStateOf(false) }

    // Track scroll position
    LaunchedEffect(Unit) {
        // In a real implementation, you'd add a scroll listener
        // For now, show after a delay
        delay(2000)
        isVisible = true
    }

    Button(
        attrs = ScrollToTopStyle.toModifier()
            .thenIf(isVisible, ScrollToTopVisibleStyle.toModifier())
            .onClick {
                window.scrollTo(
                    ScrollToOptions(
                        top = 0.0,
                        behavior = ScrollBehavior.SMOOTH
                    )
                )
            }
            .toAttrs()
    ) {
        Text("↑")
    }

    // Tooltip
    Span(attrs = BackToTopTooltipStyle.toModifier().toAttrs()) {
        Text("Back to top")
    }
}